import PropTypes from 'prop-types';
import React from 'react';
import { DownOutlined } from '@ant-design/icons';
import { Menu, Dropdown, Button as AntButton } from 'antd';
import styled, { css } from 'styled-components';
import Button from './button';

const setTextColor = (p, reverseOnHover = false) => {
  if (p.colortheme && p.theme.colors[p.colortheme] && p.theme.colors[p.colortheme].textColor) {
    return reverseOnHover ? '#fff' : p.theme.colors[p.colortheme].textColor;
  }

  return '#fff';
};

const setIconColor = (p) => {
  if (p.colortheme && p.theme.colors[p.colortheme] && p.theme.colors[p.colortheme].iconColor) {
    return p.theme.colors[p.colortheme].iconColor;
  }

  return '';
};

const setBackground = (p) => {
  if (p.colortheme && p.theme.colors[p.colortheme]) {
    return p.theme.colors[p.colortheme].backgroundColor
      ? p.theme.colors[p.colortheme].backgroundColor
      : p.theme.colors[p.colortheme];
  }

  return '#6f7e95';
};

const StyledIcon = styled.i`
  margin-right: 5px;

  color: ${p => setIconColor(p)};
`;

const AntButtonArrow = styled(AntButton)`
  border-radius: 0 4px 4px 0 !important;
`;

const Container = styled.div`
  button {
    background-color: ${p => setBackground(p)};
    color: ${p => setTextColor(p)};
    border: none;
    &:hover {
      color: white !important;
    }
  }

  div > button:first-child {
    border-right: 2px solid ${p => setTextColor(p)};
  }

  button:hover,
  button:active,
  button:disabled,
  button:focus {
    background-color: ${p => setBackground(p)};
    color: ${p => setTextColor(p)};
    outline: 0;
    opacity: ${p => p.primary ? '0.25' : '0.7'};
    border: none;
  }
`;

const StyledMenu = styled(Menu)`
  background-color: ${p => setBackground(p)} !important;
  padding: 0;

  .ant-dropdown-menu-item {
    padding-top: 8px;
    padding-bottom: 8px;
    line-height: 12px;
    color: ${p => setTextColor(p)} !important;
    margin-top: -1px;
  }

  .ant-dropdown-menu-item-selected {
    background-color: ${p => setBackground(p)} !important;
  }

  .ant-dropdown-menu-item:hover {
    background-color: ${p => p.primary ? '#0a5c75' : '#313b40'} !important;
  }

  .ant-dropdown-menu-item-disabled {
    color: rgba(255, 255, 255, 0.67) !important;
  }

  .ant-dropdown-menu-item-active {
    background-color: #313b40 !important;
    color: ${p => setTextColor(p, true)} !important;
  }

  .ant-dropdown-menu-item:last-child {
    border-radius: 0 0 4px 4px;
  }

  .ant-dropdown-menu-item:first-child {
    border-radius: 4px 4px 0 0;
  }

  .ant-dropdown-menu {
    border-radius: 4px !important;
  }
`;

const DropDownBtn = styled(Button)`
  background-color: ${p => setBackground(p)};
  > :first-child {
    height: 24px;
    display: inline-block;
    line-height: 24px;
  }
`;

const StyledDropdown = styled(Dropdown)`
  border-radius: 4px;
`;

const AntButtonStyled = styled(AntButton)`
  ${props =>
    props.fullwidth &&
    css`
      width: calc(100% - 32px);
    `}
`;

const AntButtonGroupStyled = styled(AntButton.Group)`
  ${props =>
    props.fullwidth &&
    css`
      width: 100%;
    `}
`;

const MenuItem = styled(Menu.Item)`
  white-space: nowrap;

  ${p => p.uppercase && css`
    text-transform: uppercase;
  `}
`;

const ButtonText = styled.span`
  ${p => p.uppercase && css`
    text-transform: uppercase;
  `}
`;

const DropdownButton = ({
  text,
  onClick,
  subItems,
  iconClass,
  disabled = false,
  className,
  asSeperate = true,
  uppercase = false,
  isLoading = false,
  primary = 0,
  placement = 'bottomRight',
  colortheme = '',
  fullWidth = false,
  useBodyAsParent = false,
  dataTestId = '',
}) => {
  const menu =
    <StyledMenu disabled={disabled} primary={primary} colortheme={colortheme}>
      {subItems &&
        subItems?.map((item, index) =>
          <MenuItem
            key={index}
            onClick={item.onClick}
            disabled={disabled || item.disabled}
            uppercase={uppercase}
          >
            {item.text}
          </MenuItem>,
        )}
    </StyledMenu>;
  if (asSeperate) {
    return (
      /* eslint-disable max-len */
      // As antd Dropdown.Button has layout changes since antd v3,
      // we will create it manually, based on
      // https://github.com/ant-design/ant-design/blob/master/components/dropdown/dropdown-button.tsx
      /* eslint-enable max-len */
      <Container
        className={className}
        primary={primary}
        colortheme={colortheme}
        data-test-id={dataTestId}
      >
        <AntButtonGroupStyled fullwidth={fullWidth ? 1 : 0} className='ant-dropdown-button'>
          <AntButtonStyled
            loading={isLoading}
            fullwidth={fullWidth ? 1 : 0}
            disabled={disabled}
            onClick={onClick}
          >
            {iconClass && <StyledIcon className={`fas ${iconClass}`} colortheme={colortheme} />}
            <ButtonText uppercase={uppercase}>
              {text}
            </ButtonText>
          </AntButtonStyled>
          <Dropdown
            disabled={disabled}
            overlay={menu}
            placement={placement}
            getPopupContainer={triggerNode => useBodyAsParent ?
              document.body :
              triggerNode.parentNode
            }
          >
            <AntButtonArrow icon={<DownOutlined />} />
          </Dropdown>
        </AntButtonGroupStyled>
      </Container>
    );
  }

  const trigger = disabled || subItems.every(i => i.disabled) ? ['click'] : ['hover'];
  // If the button is not 'as seperate button', so only functions as a toggle for the menu,
  // if all children are disabled, disable the button itself.
  return (
    <Container className={className} primary={primary} colortheme={colortheme}>
      <StyledDropdown
        overlay={menu}
        disabled={disabled}
        trigger={trigger}
        getPopupContainer={triggerNode => triggerNode.parentNode}
        data-test-id={dataTestId}
      >
        <DropDownBtn
          colortheme={colortheme}
          disabled={disabled || subItems.every(i => i.disabled)}
          loading={isLoading}
          primary={primary}
        >
          <ButtonText uppercase={uppercase}>
            {text}
          </ButtonText>
          {iconClass
            ? !isLoading &&
            (text ?
              <StyledIcon className={`fa ${iconClass}`} />
              :
              <i className={`fa ${iconClass}`} />
            )
            : !isLoading && <DownOutlined />}
        </DropDownBtn>
      </StyledDropdown>
    </Container>
  );
};

DropdownButton.propTypes = {
  text: PropTypes.string,
  onClick: PropTypes.func,
  subItems: PropTypes.array,
  iconClass: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  asSeperate: PropTypes.bool,
  uppercase: PropTypes.bool,
  isLoading: PropTypes.bool,
  primary: PropTypes.number,
  placement: PropTypes.string,
  colortheme: PropTypes.string,
  fullWidth: PropTypes.bool,
  useBodyAsParent: PropTypes.bool,
  dataTestId: PropTypes.string,
};

export default DropdownButton;
